import {
	PageColumns,
	PageElementsFamily,
	PageRows,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import {
	ReactElement,
	useContext,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { elementAlign, opacityHelper } from "../../lib/cssHelpers";
import { modifierObject, strings } from "@quickpoint.me/shared";
import styled, { DefaultTheme, ThemeProvider } from "styled-components";

import { ContainerSelector } from "../../state/ui";
import { ElementProps } from "../../functionalComponents/RenderPage";
import { FilterColors } from "../../state/filter";
import { ImageProps } from "../../@types/viewerati";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import { findFreeVerticalSpace } from "../../lib/findFreeVerticalSpace";
import { mainCssRules } from "../../lib/cssTemplates";
import modifierStyled from "../../lib/modifierStyled";
import { useRecoilValue } from "recoil";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";

// import { GMapLoadingStatuses, GMapLoadingStatusKey } from "../../state/preloader";

const gMapFilter = (theme: DefaultTheme) => {
	return theme.image;
};

const paddingMarginHeightKeys = [
	"paddingTop",
	"paddingBottom",
	"marginTop",
	"marginBottom",
	"paddingBlockStart",
	"paddingBlockEnd",
	"marginBlockStart",
	"marginBlockEnd",
];

const paddingMarginWidthKeys = [
	"paddingLeft",
	"paddingRight",
	"marginLeft",
	"marginRight",
	"paddingInlineStart",
	"paddingInlineEnd",
	"marginInlineStart",
	"marginInlineEnd",
];

export const getPaddingAndMargin = (ref: HTMLDivElement) => {
	const gMapStyle = window.getComputedStyle(ref);
	let height = 0;
	let width = 0;

	for (const key of paddingMarginHeightKeys) {
		// @ts-expect-error: lazy
		const val: string = gMapStyle[key];
		if (val) {
			const numericValue: number = parseInt(val.replace("px", ""));
			height = height + numericValue;
		}
	}

	for (const key of paddingMarginWidthKeys) {
		// @ts-expect-error: lazy
		const val = gMapStyle[key];
		if (val) {
			const numericValue: number = parseInt(val.replace("px", ""));
			width = width + numericValue;
		}
	}

	return { width, height };
};

const StyledGMap = styled.div<ImageProps>`
  display: flex;
  min-height: 0;
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  flex-shrink: 1;
  ${(props) => elementAlign(props.theme.align, props.$isInFlexBox)};
   
  justify-content: ${(props) =>
		props.theme.align ? props.theme.align : "center"};
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  position: relative;
  /* the map is interactive: sit above the invisible tap-navigation
     overlays or pan/zoom clicks land on Previous/Next instead */
  z-index: 101;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		theme: props.theme,
	})}`}
  ${(props) => `${modifierStyled("image:", props)}`}
  border-width:  ${(props) => {
		if (
			props.$imageWidth &&
			props.$modifier &&
			props.$modifier[modifierObject["border-thickness:"].keyword]
		) {
			return `${
				(props.$imageWidth / 400) *
				parseInt(
					props.$modifier[modifierObject["border-thickness:"].keyword] || "0",
				)
			}px`;
		}
		return "unset";
	}};
  max-width: 100%;
`;

const GoogleMap = ({
	atomId,
	pageIndex,
	type,
}: ElementProps): ReactElement<ElementProps> => {
	const containerSelector = useRecoilValue(ContainerSelector);

	const [url, setUrl] = useState("");

	const columns = useRecoilValue(PageColumns);

	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const filterColors = useRecoilValue(FilterColors);
	const pageSize = useRecoilValue(PageSizeAtom);
	const gMapId = `gMapComponent-${elementData.id}`;
	const [gMapWidthState, setGmapWidthState] = useState(0);
	const gMapRef = useRef<HTMLDivElement>(null);
	const baseFontSize = pageSize.baseFontSize;
	const tapped = elementData.tapped;
	const rows = useRecoilValue(PageRows);

	useTappedAnimation({ elRef: gMapRef, tapped, atomId });

	useLayoutEffect(() => {
		if (gMapRef.current) {
			let gMapHeight = 0;
			let gMapWidth: number | "100%" = 0;

			const [freeSpace, numberOfGMaps] = findFreeVerticalSpace(
				gMapRef,
				rows,
				pageSize,
				containerSelector,
			);
			const extras = getPaddingAndMargin(gMapRef.current);
			gMapHeight = Math.round(freeSpace / numberOfGMaps - extras.height);
			// gMapHeight = gMapHeight * pageColumns;
			gMapWidth = Math.round(pageSize.width / columns - extras.width);

			if (gMapHeight > pageSize.height) gMapHeight = pageSize.height;
			if (gMapWidth > pageSize.width) gMapWidth = pageSize.width;

			// never extend past the bottom of the slide: the free-space estimate
			// can overshoot when a sibling media element has no height yet
			const slide = gMapRef.current.closest(".display-elements-children");
			if (slide) {
				const available = Math.floor(
					slide.getBoundingClientRect().bottom -
						gMapRef.current.getBoundingClientRect().top -
						extras.height,
				);
				if (available > 0 && gMapHeight > available) gMapHeight = available;
			}
			setGmapWidthState(gMapRef.current.offsetWidth || gMapWidth);

			// map: accepts a pasted Google Maps link OR a bare place name — the
			// latter becomes an embed query. The iframe title is the accessible
			// name (WCAG 4.1.2); the place name makes the better one.
			const contentText = String(elementData.content ?? "").trim();
			const isMapUrl = /^https?:\/\//.test(contentText);
			const mapSrc = isMapUrl
				? contentText
				: `https://maps.google.com/maps?q=${encodeURIComponent(contentText)}&output=embed`;
			const mapTitle = (
				isMapUrl ? "Embedded map" : `Map: ${contentText}`
			).replace(/"/g, "&quot;");
			setUrl(
				/* width tracks the container (which the slide CSS constrains to
				   the text measure) so late layout passes don't rebuild a
				   full-bleed frame */
				`<iframe title="${mapTitle}" src="${mapSrc.replace(/"/g, "&quot;")}" height=${gMapHeight} style="border:0;width:100%;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`,
			);
		}
	}, [columns, containerSelector, elementData.content, pageSize, rows]);

	return (
		<ThemeProvider theme={gMapFilter}>
			<StyledGMap
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$imageWidth={gMapWidthState}
				$isInFlexBox={isInFlexBox}
				$isInLandscape={pageSize.landscape}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				aria-hidden={!elementData.tapped}
				className={`${strings.css.pageElement}  ${strings.css.dynamicResize} gMap`}
				data-atom-id={atomId}
				data-testid={`${type}${atomId}`}
				id={gMapId}
				ref={gMapRef}
				$onePercentWidth={pageSize.onePercentWidth}
			>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: google maps needs it */}
				<div dangerouslySetInnerHTML={{ __html: url }} />
			</StyledGMap>
		</ThemeProvider>
	);
};

export default GoogleMap;
